<template>
  <!-- Only an empty tile is a button: once there is a cover, the gallery on the details tab is where it changes -->
  <PropertyPhotoPreview
    :image-url="pendingCover ?? building.coverImage ?? undefined"
    :size="size"
    :action-label="building.coverImage ? undefined : 'Add cover'"
    @click="pickCover"
  />
</template>

<script setup lang="ts">
import { useFileDialog } from '@vueuse/core';
import { onUnmounted, ref } from 'vue';

import { extractErrorMessage, useAlertsStore } from '@tallo/design-system';
import { checkImageFile } from '@tallo/file-manager';
import { BuildingDto, useUploadBuildingCover } from '@tallo/investor/property';
import { PropertyPhotoPreview } from '@tallo/property';

const props = defineProps<{ building: BuildingDto; size: 'sm' | 'md' }>();

// The cover is the first gallery photo, so it takes what the gallery takes; the route refuses anything bigger
const maxCoverMb = 25;

const alertsStore = useAlertsStore();
const { mutateAsync: uploadCover } = useUploadBuildingCover();
const fileDialog = useFileDialog({ accept: 'image/*', multiple: false, reset: true });
/** The picked photo, shown from the moment it is picked until the upload answers */
const pendingCover = ref<string | null>(null);

fileDialog.onChange(async (files) => {
  const image = files?.[0];

  if (!image) {
    return;
  }

  const refusal = checkImageFile(image, maxCoverMb);

  if (refusal) {
    alertsStore.error(refusal);
    return;
  }

  pendingCover.value = URL.createObjectURL(image);

  try {
    await uploadCover({ buildingId: props.building.id, image });
  } catch (error) {
    // A colleague who does not work on the building gets the access dialog instead, which holds this back
    alertsStore.error(extractErrorMessage(error, "Couldn't upload the cover"));
  } finally {
    releasePendingCover();
  }
});

function pickCover() {
  // One photo at a time: the tile waits for the upload in flight
  if (!pendingCover.value) {
    fileDialog.open();
  }
}

function releasePendingCover() {
  if (pendingCover.value) {
    URL.revokeObjectURL(pendingCover.value);
    pendingCover.value = null;
  }
}

onUnmounted(releasePendingCover);
</script>
